/**
 * Recently Viewed Products
 * Tracks product views and displays them in the recently-viewed section
 * Logic: Show recently viewed products first, then fill with static products (no duplicates)
 * Once user has viewed more than max products, show only recently viewed
 */

class RecentlyViewed {
  constructor() {
    this.storageKey = 'shopify_recently_viewed_products';
    this.maxProducts = 50; // Maximum products to store in localStorage
    this.init();
  }

  init() {
    // Track product view on product pages
    if (this.isProductPage()) {
      this.trackProductView();
    }

    // Update recently viewed sections on page load
    this.updateRecentlyViewedSections();
  }

  /**
   * Check if current page is a product page
   */
  isProductPage() {
    return (
      document.querySelector('product-info[data-product-id]') !== null ||
      window.location.pathname.includes('/products/') ||
      document.documentElement.classList.contains('product')
    );
  }

  /**
   * Track product view on product page
   */
  trackProductView() {
    const productInfo = document.querySelector('product-info[data-product-id]');
    if (!productInfo) return;

    const productId = productInfo.dataset.productId;
    const productUrl = productInfo.dataset.url || window.location.pathname;
    
    // Wait a bit to ensure page is fully loaded
    setTimeout(() => {
      // Get product data from the page
      const productData = this.getProductDataFromPage();
      
      if (productData && productId) {
        const handle = productData.handle || this.extractHandleFromUrl(productUrl);
        
        // Only track if we have essential data
        if (handle && productId) {
          this.addProductToRecentlyViewed({
            id: productId,
            handle: handle,
            title: productData.title || document.querySelector('h1')?.textContent?.trim() || '',
            image: productData.image || this.getProductImage(),
            secondaryImage: productData.secondaryImage || this.getSecondaryProductImage(),
            price: productData.price || this.getProductPrice() || '',
            compareAtPrice: productData.compareAtPrice || '',
            url: productUrl,
            available: productData.available !== false,
            vendor: productData.vendor || ''
          });
        }
      }
    }, 500); // Small delay to ensure page elements are ready
  }

  /**
   * Extract product data from the current page
   */
  getProductDataFromPage() {
    try {
      const productInfo = document.querySelector('product-info[data-product-id]');
      if (!productInfo) return null;

      // Try to get product data from structured data
      const structuredData = document.querySelector('script[type="application/ld+json"]');
      if (structuredData) {
        const data = JSON.parse(structuredData.textContent);
        if (data['@type'] === 'Product' || (Array.isArray(data) && data[0]?.['@type'] === 'Product')) {
          const productData = Array.isArray(data) ? data.find(item => item['@type'] === 'Product') : data;
          if (productData) {
            const imageUrl = Array.isArray(productData.image) ? productData.image[0] : productData.image;
            return {
              handle: this.extractHandleFromUrl(productData.url || window.location.pathname),
              title: productData.name || '',
              image: imageUrl || '',
              price: productData.offers?.price || productData.offers?.lowPrice || '',
              available: productData.offers?.availability?.includes('InStock') !== false,
              vendor: productData.brand?.name || productData.manufacturer?.name || ''
            };
          }
        }
      }

      // Fallback: extract from page elements
      const handle = this.extractHandleFromUrl(window.location.pathname);
      const titleEl = document.querySelector('h1, [data-product-title], .product__title h1');
      const priceEl = document.querySelector('[data-product-price], .price, .product__price, .price-item');
      
      return {
        handle: handle,
        title: titleEl?.textContent?.trim() || '',
        image: this.getProductImage(),
        price: priceEl?.textContent?.trim() || '',
        available: !document.querySelector('[data-product-sold-out], .product-form__submit[disabled]')
      };
    } catch (e) {
      console.error('Error extracting product data:', e);
      return null;
    }
  }

  /**
   * Extract product handle from URL
   */
  extractHandleFromUrl(url) {
    const match = url.match(/\/products\/([^\/\?#]+)/);
    return match ? match[1] : '';
  }

  /**
   * Get product image from page
   */
  getProductImage() {
    const img = document.querySelector('product-media-gallery img, .product__media img, [data-product-media] img');
    return img ? img.src : '';
  }

  /**
   * Get secondary product image from page (second image in gallery)
   */
  getSecondaryProductImage() {
    const images = document.querySelectorAll('product-media-gallery img, .product__media img, [data-product-media] img');
    if (images.length > 1) {
      return images[1].src || '';
    }
    return '';
  }

  /**
   * Get product price from page
   */
  getProductPrice() {
    const priceElement = document.querySelector('[data-product-price], .price, .product__price, .price-item');
    return priceElement ? priceElement.textContent.trim() : '';
  }

  /**
   * Add product to recently viewed list
   */
  addProductToRecentlyViewed(product) {
    if (!product.id || !product.handle) return;

    try {
      let recentlyViewed = this.getRecentlyViewedProducts();
      
      // Remove if already exists (to move to top)
      recentlyViewed = recentlyViewed.filter(p => p.id !== product.id);
      
      // Add to beginning
      recentlyViewed.unshift(product);
      
      // Limit to maxProducts
      if (recentlyViewed.length > this.maxProducts) {
        recentlyViewed = recentlyViewed.slice(0, this.maxProducts);
      }
      
      // Save to localStorage
      localStorage.setItem(this.storageKey, JSON.stringify(recentlyViewed));
      
      // Update sections if they exist
      this.updateRecentlyViewedSections();
    } catch (e) {
      console.error('Error saving recently viewed product:', e);
    }
  }

  /**
   * Get recently viewed products from localStorage
   */
  getRecentlyViewedProducts() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error reading recently viewed products:', e);
      return [];
    }
  }

  /**
   * Update all recently viewed sections on the page
   */
  updateRecentlyViewedSections() {
    const sections = document.querySelectorAll('[id^="recently-viewed-"]');
    sections.forEach(section => {
      this.updateSection(section);
    });
  }

  /**
   * Update a specific recently viewed section
   */
  async updateSection(section) {
    const recentlyViewed = this.getRecentlyViewedProducts();
    const productsToShow = parseInt(section.dataset.productsToShow) || 4;
    
    // Get static products from data attribute
    let staticProducts = [];
    try {
      const staticProductsData = section.dataset.staticProducts;
      if (staticProductsData) {
        staticProducts = JSON.parse(staticProductsData);
      }
    } catch (e) {
      console.error('Error parsing static products:', e);
    }

    // Determine which products to display
    let productsToDisplay = [];
    
    if (recentlyViewed.length === 0) {
      // No recently viewed products, show static products only
      productsToDisplay = staticProducts.slice(0, productsToShow);
    } else if (recentlyViewed.length < productsToShow) {
      // Mix: recently viewed + static products (excluding duplicates)
      const recentlyViewedIds = new Set(recentlyViewed.map(p => p.id));
      
      // Get static products that are not in recently viewed
      const availableStaticProducts = staticProducts.filter(
        p => !recentlyViewedIds.has(p.id)
      );
      
      // Combine: recently viewed first, then fill with static products
      productsToDisplay = [
        ...recentlyViewed,
        ...availableStaticProducts.slice(0, productsToShow - recentlyViewed.length)
      ];
    } else {
      // User has viewed more than max products, show only recently viewed
      productsToDisplay = recentlyViewed.slice(0, productsToShow);
    }

    // If we have products to display, render them
    if (productsToDisplay.length > 0) {
      // Fetch full product data if needed
      const productsWithFullData = await this.fetchProductData(productsToDisplay);
      this.renderProducts(section, productsWithFullData);
    }
  }

  /**
   * Fetch full product data by fetching product pages and extracting product card HTML
   */
  async fetchProductData(products) {
    // If products already have sufficient data, return as is
    const needsFetch = products.some(p => !p.image || !p.price);
    
    if (!needsFetch) {
      return products;
    }

    const productHandles = products.map(p => p.handle).filter(Boolean);
    
    try {
      // Fetch product pages and extract product data
      const responses = await Promise.all(
        productHandles.map(async (handle) => {
          try {
            const response = await fetch(`/products/${handle}`);
            if (!response.ok) return null;
            
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            
            // Extract product data from the page
            const structuredData = doc.querySelector('script[type="application/ld+json"]');
            
            let productData = {};
            
            // Try structured data first
            if (structuredData) {
              try {
                const data = JSON.parse(structuredData.textContent);
                const product = Array.isArray(data) ? data.find(item => item['@type'] === 'Product') : data;
                if (product && product['@type'] === 'Product') {
                  const imageUrl = Array.isArray(product.image) ? product.image[0] : product.image;
                  productData = {
                    image: imageUrl || '',
                    price: product.offers?.price || product.offers?.lowPrice || '',
                    title: product.name || '',
                    available: product.offers?.availability?.includes('InStock') !== false,
                    vendor: product.brand?.name || ''
                  };
                }
              } catch (e) {
                // Continue with fallback
              }
            }
            
            // Fallback: extract from page elements
            if (!productData.image) {
              const img = doc.querySelector('product-media-gallery img, .product__media img');
              productData.image = img ? img.src : '';
            }
            
            // Get secondary image (second image in gallery)
            const images = doc.querySelectorAll('product-media-gallery img, .product__media img');
            if (images.length > 1 && !productData.secondaryImage) {
              productData.secondaryImage = images[1].src || '';
            }
            
            if (!productData.price) {
              const priceEl = doc.querySelector('[data-product-price], .price, .product__price');
              productData.price = priceEl ? priceEl.textContent.trim() : '';
            }
            
            if (!productData.title) {
              const titleEl = doc.querySelector('h1, [data-product-title]');
              productData.title = titleEl ? titleEl.textContent.trim() : '';
            }
            
            return productData;
          } catch (e) {
            console.error(`Error fetching product ${handle}:`, e);
            return null;
          }
        })
      );

      return products.map((product, index) => {
        const fetchedData = responses[index];
        if (fetchedData) {
          return {
            ...product,
            image: product.image || fetchedData.image || '',
            secondaryImage: product.secondaryImage || fetchedData.secondaryImage || '',
            price: product.price || fetchedData.price || '',
            title: product.title || fetchedData.title || product.title,
            available: product.available !== false && (fetchedData.available !== false),
            vendor: product.vendor || fetchedData.vendor || ''
          };
        }
        return product;
      });
    } catch (e) {
      console.error('Error fetching product data:', e);
      return products;
    }
  }

  /**
   * Render products in the section
   */
  renderProducts(section, products) {
    const container = section.querySelector('[id^="recently-viewed-products-"]');
    if (!container) return;

    const slider = container.querySelector('slider-component');
    const productList = container.querySelector('ul.grid');
    if (!productList) return;

    // Get section settings
    const settings = {
      columnsDesktop: parseInt(section.dataset.columnsDesktop) || 4,
      columnsMobile: parseInt(section.dataset.columnsMobile) || 2,
      imageRatio: section.dataset.imageRatio || 'adapt',
      imageShape: section.dataset.imageShape || 'default',
      showSecondaryImage: section.dataset.showSecondaryImage === 'true',
      showVendor: section.dataset.showVendor === 'true',
      showRating: section.dataset.showRating === 'true',
      quickAdd: section.dataset.quickAdd || 'none',
      sectionId: section.dataset.sectionId
    };

    // Remove all existing products (both static and previously rendered)
    const existingProducts = productList.querySelectorAll('li');
    existingProducts.forEach(el => el.remove());

    // Render products
    products.forEach((product, index) => {
      const listItem = this.createProductCard(product, settings, index);
      productList.appendChild(listItem);
      
      // For 'adapt' ratio, calculate actual ratio after image loads
      if (settings.imageRatio === 'adapt') {
        const img = listItem.querySelector('img');
        if (img) {
          img.addEventListener('load', () => {
            const ratio = this.calculateImageRatio(img);
            const ratioPercent = (1 / ratio) * 100;
            const card = listItem.querySelector('.card');
            const cardInner = listItem.querySelector('.card__inner');
            if (card) card.style.setProperty('--ratio-percent', `${ratioPercent}%`);
            if (cardInner) cardInner.style.setProperty('--ratio-percent', `${ratioPercent}%`);
          });
        }
      }
    });

    // Update slider counter if exists
    const sliderCounter = container.querySelector('.slider-counter--total');
    if (sliderCounter) {
      sliderCounter.textContent = products.length;
    }

    // Reinitialize slider if needed
    if (slider && typeof window.SliderComponent !== 'undefined') {
      // Trigger slider reinitialization if the component supports it
      slider.dispatchEvent(new CustomEvent('slider:update'));
    }
  }

  /**
   * Create product card HTML element
   */
  createProductCard(product, settings, index) {
    const li = document.createElement('li');
    li.className = `grid__item${settings.enableDesktopSlider ? ' slider__slide' : ''}`;
    li.id = `Slide-${settings.sectionId}-${index + 1}`;
    li.setAttribute('data-product-id', product.id);
    li.setAttribute('data-product-handle', product.handle);

    // Create product card HTML matching card-product snippet structure
    const imageUrl = product.image || '';
    const secondaryImageUrl = product.secondaryImage || '';
    const imageSrcset = this.generateImageSrcset(imageUrl);
    const secondaryImageSrcset = secondaryImageUrl ? this.generateImageSrcset(secondaryImageUrl) : '';
    const priceHtml = this.formatPrice(product.price, product.compareAtPrice);
    const cardStyle = window.theme?.settings?.card_style || 'standard';
    const ratioPercent = this.getRatioPercent(settings.imageRatio, imageUrl);
    const hasImage = !!imageUrl;
    const showSecondary = settings.showSecondaryImage && secondaryImageUrl;
    
    li.innerHTML = `
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="card card--${cardStyle} ${hasImage ? 'card--media' : 'card--text'} ${cardStyle === 'card' ? 'color-scheme-1 gradient' : ''} ${settings.imageShape && settings.imageShape !== 'default' ? 'card--shape' : ''}" style="--ratio-percent: ${ratioPercent}%;">
          <div class="card__inner ${cardStyle === 'standard' ? 'color-scheme-1 gradient' : ''} ${hasImage || cardStyle === 'standard' ? 'ratio' : ''}" style="--ratio-percent: ${ratioPercent}%;">
            ${hasImage ? `
              <div class="card__media ${settings.imageShape && settings.imageShape !== 'default' ? `shape--${settings.imageShape} color-scheme-1 gradient` : ''}">
                <a href="${product.url}" class="full-unstyled-link">
                  <div class="media media--transparent media--hover-effect">
                    <img
                      srcset="${imageSrcset || imageUrl}"
                      src="${imageUrl}"
                      sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                      alt="${this.escapeHtml(product.title)}"
                      class="motion-reduce"
                      loading="${index < settings.columnsDesktop ? 'eager' : 'lazy'}"
                      width="533"
                      height="533"
                    >
                    ${showSecondary ? `
                      <img
                        srcset="${secondaryImageSrcset || secondaryImageUrl}"
                        src="${secondaryImageUrl}"
                        sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                        alt="${this.escapeHtml(product.title)}"
                        class="motion-reduce"
                        loading="lazy"
                        width="533"
                        height="533"
                      >
                    ` : ''}
                  </div>
                </a>
              </div>
            ` : ''}
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading${hasImage || cardStyle === 'standard' ? ' h5' : ''}" id="title-${settings.sectionId}-${product.id}">
                  <a href="${product.url}" id="CardLink-${settings.sectionId}-${product.id}" class="full-unstyled-link" aria-labelledby="CardLink-${settings.sectionId}-${product.id} Badge-${settings.sectionId}-${product.id}">
                    ${this.escapeHtml(product.title)}
                  </a>
                </h3>
              </div>
            </div>
            <div class="card__badge">
              ${!product.available ? `
                <span id="Badge-${settings.sectionId}-${product.id}" class="badge badge--bottom-left color-scheme-2">
                  Sold out
                </span>
              ` : product.compareAtPrice && parseFloat(product.compareAtPrice.replace(/[^0-9.]/g, '')) > parseFloat((product.price || '0').replace(/[^0-9.]/g, '')) ? `
                <span id="Badge-${settings.sectionId}-${product.id}" class="badge badge--bottom-left color-scheme-2">
                  On sale
                </span>
              ` : ''}
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading${hasImage || cardStyle === 'standard' ? ' h5' : ''}" id="title-${settings.sectionId}-${product.id}">
                <a href="${product.url}" id="CardLink-${settings.sectionId}-${product.id}" class="full-unstyled-link" aria-labelledby="CardLink-${settings.sectionId}-${product.id} Badge-${settings.sectionId}-${product.id}">
                  ${this.escapeHtml(product.title)}
                </a>
              </h3>
              <div class="card-information">
                ${settings.showVendor && product.vendor ? `
                  <span class="visually-hidden">Vendor</span>
                  <div class="caption-with-letter-spacing light">${this.escapeHtml(product.vendor)}</div>
                ` : ''}
                ${priceHtml}
              </div>
            </div>
          </div>
          <div class="card__badge">
            ${!product.available ? `
              <span id="Badge-${settings.sectionId}-${product.id}" class="badge badge--bottom-left color-scheme-2">
                Sold out
              </span>
            ` : product.compareAtPrice && parseFloat(product.compareAtPrice.replace(/[^0-9.]/g, '')) > parseFloat((product.price || '0').replace(/[^0-9.]/g, '')) ? `
              <span id="Badge-${settings.sectionId}-${product.id}" class="badge badge--bottom-left color-scheme-2">
                On sale
              </span>
            ` : ''}
          </div>
        </div>
      </div>
    `;

    return li;
  }

  /**
   * Generate image srcset for Shopify images
   */
  generateImageSrcset(imageUrl) {
    if (!imageUrl) return '';
    
    // If it's a Shopify CDN URL, generate srcset
    if (imageUrl.includes('cdn.shopify.com')) {
      const widths = [165, 360, 533, 720, 940, 1066];
      // Try to preserve the image format
      const formatMatch = imageUrl.match(/\.(jpg|jpeg|png|webp)/i);
      const format = formatMatch ? formatMatch[1].toLowerCase() : 'jpg';
      
      return widths.map(w => {
        // Replace existing size suffix or add new one
        const sizedUrl = imageUrl.replace(/_\d+x\./i, `_${w}x.`).replace(/\.(jpg|jpeg|png|webp)/i, `_${w}x.${format}`);
        return `${sizedUrl} ${w}w`;
      }).join(', ');
    }
    
    return '';
  }

  /**
   * Get ratio percent for image
   * Returns the CSS value for --ratio-percent (which is 1/ratio * 100)
   * For 'adapt', we'll calculate it dynamically from the image when it loads
   */
  getRatioPercent(imageRatio, imageUrl = '') {
    let ratio = 1;
    
    if (imageRatio === 'portrait') {
      ratio = 0.8;
    } else if (imageRatio === 'square') {
      ratio = 1;
    } else if (imageRatio === 'adapt') {
      // For adapt, we'll set a default and let CSS handle it
      // The actual aspect ratio will be maintained by the image itself
      ratio = 1; // This will be overridden by the image's natural aspect ratio
    }
    
    // Calculate CSS value: 1 / ratio * 100
    return (1 / ratio) * 100;
  }

  /**
   * Calculate image aspect ratio from image element
   */
  calculateImageRatio(img) {
    if (img.naturalWidth && img.naturalHeight) {
      return img.naturalWidth / img.naturalHeight;
    }
    return 1;
  }

  /**
   * Format price HTML
   */
  formatPrice(price, compareAtPrice) {
    if (!price) return '';
    
    const priceFormatted = this.formatMoney(price);
    const compareAtPriceFormatted = compareAtPrice ? this.formatMoney(compareAtPrice) : '';
    
    if (compareAtPriceFormatted && this.parsePrice(compareAtPrice) > this.parsePrice(price)) {
      return `
        <div class="price">
          <s class="price-item price-item--regular">${compareAtPriceFormatted}</s>
          <span class="visually-hidden">Sale price</span>
          <span class="price-item price-item--sale">${priceFormatted}</span>
        </div>
      `;
    }
    
    return `
      <div class="price">
        <span class="price-item">${priceFormatted}</span>
      </div>
    `;
  }

  /**
   * Parse price string to number
   */
  parsePrice(priceStr) {
    if (typeof priceStr === 'number') return priceStr;
    return parseFloat((priceStr || '0').replace(/[^0-9.]/g, '')) || 0;
  }

  /**
   * Format money (simplified - uses Shopify's money format if available)
   */
  formatMoney(price) {
    if (typeof price === 'string') {
      // If already formatted, return as is
      if (price.includes('$') || price.includes('€') || price.includes('£') || price.includes('¥')) {
        return price;
      }
      // Try to parse as number
      const numPrice = parseFloat(price);
      if (isNaN(numPrice)) return price;
      price = numPrice;
    }
    
    // If price looks like it's in cents (large number), convert to dollars
    if (price > 1000 && price % 100 === 0) {
      price = price / 100;
    }
    
    // Use Shopify's money format if available
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(price * 100); // Shopify expects cents
    }
    
    // Fallback to Intl formatter
    try {
      const currency = window.Shopify?.currency?.active || 
                      document.documentElement.lang?.split('-')[1]?.toUpperCase() || 
                      'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency
      }).format(price);
    } catch (e) {
      return `$${price.toFixed(2)}`;
    }
  }

  /**
   * Escape HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.recentlyViewed = new RecentlyViewed();
  });
} else {
  window.recentlyViewed = new RecentlyViewed();
}

// Also initialize when sections are loaded dynamically (Shopify theme editor)
document.addEventListener('shopify:section:load', () => {
  if (window.recentlyViewed) {
    window.recentlyViewed.updateRecentlyViewedSections();
  }
});

